import { type Maybe } from "@digits-graphql/frontend/graphql-public"

/** Returns two arrays given the input array and predicate.
 *
 * The first returned array contains all items for which the predicate returned true.
 *
 * The second contains the rest of the items. */
export function partition<T>(items: T[], predicate: (item: T) => boolean): [T[], T[]] {
  return items.reduce(
    (partitions, item) => {
      const part = predicate(item) ? partitions[0] : partitions[1]
      part.push(item)
      return partitions
    },
    [[] as T[], [] as T[]]
  )
}

/**
 * Ensures that the provided argument is an array
 */
export function ensureArray<T>(itemOrItems: T | T[]): T[] {
  if (Array.isArray(itemOrItems)) return itemOrItems
  return [itemOrItems]
}

/**
 * Returns a first-element indexable array type when the array has at least one element
 *
 * Note: Technically this type could be incorrect if the array is sparse
 */
export function hasFirstElement<T>(elements?: T[] | null): elements is [T] {
  return Boolean(elements && elements.length > 0)
}

/**
 * Type guard function that checks if an array has exactly N elements.
 */
export function hasExactlyN<T>(elements: Maybe<T[]> | undefined, n: number): elements is T[] {
  return Boolean(elements && elements.length === n)
}

/**
 * Returns a first-element indexable array type when the array has exactly one element
 */
export function hasExactlyOne<T>(elements: Maybe<T[]> | undefined): elements is [T] {
  return hasExactlyN(elements, 1)
}

/** Returns a random element from the array */
export function getRandomElement<T>(elements: T[]): T {
  return elements[Math.floor(Math.random() * elements.length)] as T
}

/** Shuffles an array using the Fisher-Yates (Knuth) shuffle algorithm.
 *
 * Returns a shallow copy of the array.
 */
export function shuffle<T>(elements: T[]): T[] {
  const shuffled = [...elements]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j] as T, shuffled[i] as T]
  }
  return shuffled
}

export function areEqual<T>(arr1: T[], arr2: T[]): boolean {
  // Check if arrays have the same length
  if (arr1.length !== arr2.length) {
    return false
  }

  // Compare each element
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false
    }
  }

  return true
}

/**
 * Compares two arrays to check if they contain the same items regardless of order.
 *
 * @param arr1 - First array to compare
 * @param arr2 - Second array to compare
 * @returns true if both arrays contain the same items (order doesn't matter), false otherwise
 *
 * @example
 * haveSameItems([1, 3, 2], [1, 2, 3]) // returns true
 * haveSameItems([1, 2, 3], [1, 2, 4]) // returns false
 * haveSameItems([1, 2, 2], [1, 2]) // returns false (different counts)
 */
export function haveSameItems<T>(arr1: T[], arr2: T[]): boolean {
  // Check if arrays have the same length
  if (arr1.length !== arr2.length) {
    return false
  }

  // Create frequency maps for both arrays
  const frequencyMap1 = new Map<T, number>()
  const frequencyMap2 = new Map<T, number>()

  // Count occurrences in first array
  for (const item of arr1) {
    frequencyMap1.set(item, (frequencyMap1.get(item) || 0) + 1)
  }

  // Count occurrences in second array
  for (const item of arr2) {
    frequencyMap2.set(item, (frequencyMap2.get(item) || 0) + 1)
  }

  // Compare frequency maps
  if (frequencyMap1.size !== frequencyMap2.size) {
    return false
  }

  for (const [item, count] of frequencyMap1) {
    if (frequencyMap2.get(item) !== count) {
      return false
    }
  }

  return true
}
